import { DOCUMENT } from '@angular/common';
import {
    Component,
    ElementRef,
    EventEmitter,
    Inject,
    Input,
    OnInit,
    OnDestroy,
    Output,
    Renderer2
} from '@angular/core';
import { Router } from '@angular/router';

import { select } from '@angular-redux/store';
import { TranslateService } from '@ngx-translate/core';

import {
    ApplicationFacade,
    CustomerFacade,
    CustomerService,
    LoginFacade,
    NotificationFacade,
    SessionService
} from '@ridango/components';
import {
    AppStartupService,
    BaseComponent,
    Language,
    LogService,
    NetworkService,
    OnboardingService
} from '@ridango/core';
import { ThemeFeature } from '@ridango/core/lib/enums/features.enum';
import { HeaderMenuService } from '../../services/header-menu.service';

import { HelperService } from '../../services/helper.service';
import { OnboardingType } from '@ridango/widgets';
import Env from '@ridango/core/env';
import { take, takeUntil } from 'rxjs/operators';
import { LocaleService } from '../../../services/locale.service';
import { Observable } from 'rxjs';
import { AnalyticsService } from '@ridango/core/lib/services/analytics.service';
import { TrackedEventCategory } from '@ridango/core/lib/enums/analytics/tracked-event-categories.enum';
import { TrackedEventAction } from '@ridango/core/lib/enums/analytics/tracked-event-action.enum';

@Component({
    selector: 'gl-header',
    templateUrl: './header.component.html'
})
export class HeaderComponent extends BaseComponent implements OnInit, OnDestroy {
    profile$: Observable<any> = this.customerFacade.profile$;
    @Input() selected: Language;
    @Input() isLandingPage: boolean;
    themeFeature = ThemeFeature;
    isOpened = false;
    languages = [];

    loggedInCustomer = '';
    assetsPath$ = this.applicationFacade.assetsPath$;
    highContrast$ = this.applicationFacade.highContrast$;

    @select(['auth', 'customer', 'data']) customer$;
    @select(['core', 'selectedLanguage']) selectedLanguage$;
    @select(['core', 'region', 'languages']) languages$;

    @Output() requestLanguageChange = new EventEmitter();
    @Output() highContrastToggle = new EventEmitter();

    networkStatus$ = this.networkService.getStatus();
    private readonly LOG_OUT = 'Log out';

    constructor(
        private appConfig: AppStartupService,
        public applicationFacade: ApplicationFacade,
        public eRef: ElementRef,
        private ls: LogService,
        @Inject(DOCUMENT) public document: Document,
        public router: Router,
        public localeService: LocaleService,
        public ridangoAuth: SessionService,
        private notification: NotificationFacade,
        public renderer: Renderer2,
        public helper: HelperService,
        private loginFacade: LoginFacade,
        public translate: TranslateService,
        @Inject('ENV') public env: Env,
        private onboardingService: OnboardingService,
        private customerService: CustomerService,
        private customerFacade: CustomerFacade,
        private networkService: NetworkService,
        protected headerMenuService: HeaderMenuService,
        private analyticsService: AnalyticsService
    ) {
        super();
    }

    ngOnInit() {
        if (!this.selected) {
            this.selected = this.appConfig.getCurrentLanguage();
        }

        this.languages = this.appConfig.getRegion().languages;
        this.customer$.pipe(takeUntil(this.componentDestroyed$)).subscribe((c) => {
            if (this.env.HEADER.SHOW_EMAIL_INSTEAD_NAME) {
                this.loggedInCustomer = this.ridangoAuth.getLoggedInEmail();
            } else if (c && c.name) {
                this.loggedInCustomer = c.name;
            } else if (c && c.email) {
                this.loggedInCustomer = c.email;
            } else {
                this.loggedInCustomer = this.ridangoAuth.getLoggedInUserName();
            }

            if (!this.loggedInCustomer) {
                this.profile$.pipe(take(1), takeUntil(this.componentDestroyed$)).subscribe((profile) => {
                    this.loggedInCustomer = profile?.name || profile?.email;
                    if (this.loggedInCustomer) {
                        this.ridangoAuth.setLoggedInUserName(this.loggedInCustomer);
                    }
                });
            }
            this.ls.log(this, 'ngOnInit loggedInCustomer', this.loggedInCustomer);
        });
    }

    toggleMobileMenu() {
        this.applicationFacade.setMobileMenuOpen(!this.applicationFacade.isMenuOpen());

        if (this.applicationFacade.isMenuOpen()) {
            this.renderer.addClass(this.document.body, 'lock-scrolling');
        } else {
            if (this.env.MOBILE_MENU_CUSTOMIZATION?.IS_PAGE) {
                this.localeService.menuChangeLanguage = this.translate.currentLang;
                window.history.back();
            }

            this.renderer.removeClass(this.document.body, 'lock-scrolling');
        }
    }

    closeMobileMenu() {
        this.applicationFacade.setMobileMenuOpen(false);
        this.renderer.removeClass(this.document.body, 'lock-scrolling');
    }

    isMenuOpen() {
        return this.applicationFacade.isMenuOpen();
    }

    handleLanguageChange(lang: Language) {
        this.closeMobileMenu();
        this.requestLanguageChange.emit(lang);
        const token = this.ridangoAuth.getLocalAccessToken();
        if (this.env.SEND_CUSTOMER_LANGUAGE_TO_BACKEND && token && lang) {
            this.customerFacade.sendCustomerLang(lang.code);
        }
        // if ( this.selected.code !== lang.code ) {
        //     // TODO: handle state savig here
        //     this.ls.log( this, 'handleLanguageChange', this.router.url );
        //     saveState( this.ngRedux.getState() );
        //     this.selected = lang;
        //     if ( this.environment.production ) {
        //         this.document.location.href = this.environment.BASE_URL + lang.code + this.router.url;
        //     } else {
        //         this.document.location.href = this.environment.BASE_URL + this.router.url;
        //     }
        // }
    }

    async logout(): Promise<void> {
        this.analyticsService.eventTrack(TrackedEventAction.CLICKED, {
            category: TrackedEventCategory.MENU,
            name: this.LOG_OUT
        });
        this.handleLogout();
        await this.router.navigate([this.translate.currentLang, 'login']);
    }

    handleLogout() {
        this.ridangoAuth.signOut();
        this.loginFacade.logout();
        this.notification.success(this.translate.instant('LOCAL_LOGOUT_SUCCESS'), {
            showAfterRouteChange: true,
            title: this.env.MOBILE_MENU_CUSTOMIZATION?.LOGOUT_TITLE_NOTIFICATION
                ? this.translate.instant('TITLE_LOGOUT_SUCCESS')
                : this.translate.instant('success')
        });

        if (this.env.ONBOARDING?.LOGIN?.REQUIRE_LOGIN) {
            this.onboardingService.onboardingStatus = OnboardingType.LOGIN_PAGE;
        }

        this.helper.cleanupAfterLogout();
        this.closeMobileMenu();
    }

    navigateLogin() {
        this.router.navigate([this.translate.currentLang, 'login']);
    }

    onMobileMenuClick() {
        this.applicationFacade.setMobileMenuOpen(!this.applicationFacade.isMenuOpen());
    }

    openClicked() {
        /*this.isOpened = true;*/
        this.isOpened = !this.isOpened;
    }

    toggleTheme() {
        this.highContrastToggle.emit();
    }
}
